import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetPublicationTypesQuery } from '../../../api/globalDataApi';
import {
  useAddPublicationToVariantMutation,
  useGetVariantDetailQuery,
  useGetVariantReferencesQuery,
  useUpdatePublicationToVariantMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES } from '../../constants';
import { PubMedPublication, Reference } from '../../redux';

interface UseReferenceModalLogicProps {
  closeModal: () => void;
  type: 'add' | 'edit';
  pubmedPublication?: PubMedPublication;
  currentValues?: Reference;
  stageType?: string;
}

/**
 * Custom hook that encapsulates all the business logic for ReferenceModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useReferenceModalLogic = ({
  closeModal,
  type,
  pubmedPublication,
  currentValues,
  stageType,
}: UseReferenceModalLogicProps) => {
  const {
    isLoading,
    setIsLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: NOTIFICATION_MESSAGES.REFERENCE_SAVED,
    stageType,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  const { data: publicationTypes = [] } = useGetPublicationTypesQuery();
  // Subscribe to the references cache entry so it stays live while the modal
  // is open and is invalidated/refetched automatically when either mutation below succeeds
  useGetVariantReferencesQuery(
    { variant_id: variant?.id },
    { skip: !variant?.id },
  );

  // Mutation hooks return [trigger, result] tuples.
  // invalidatesTags on each mutation causes RTK Query to refetch VariantReferences
  // automatically on success — no manual refetch needed
  const [addPublicationToVariantMutation] =
    useAddPublicationToVariantMutation();
  const [updatePublicationToVariantMutation] =
    useUpdatePublicationToVariantMutation();

  const isAddMode = type === 'add';

  const boolOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  // Return title parts separately so JSX can be constructed in the component
  const titleCitekey =
    pubmedPublication?.citekey || currentValues?.publication.citekey;
  const titleText =
    pubmedPublication?.title || currentValues?.publication.title;

  // createModalOpts comes from useModalForm (global) â€“ no local override needed.

  // Normalizes a stored type value.
  // Bug fix DNAWZ-514: the publication-types API and old DB records stored "select reference type"
  // (the display label) but the update endpoint validates against "select_reference_type" (the key).
  const normalizeTypeValue = useCallback(
    (type: string | null | undefined): string | null => {
      if (!type) {
        return null;
      }
      if (type === 'select reference type') {
        return 'select_reference_type';
      }
      if (publicationTypes.find((pt: any) => pt.value === type)) {
        return type;
      }
      return (
        publicationTypes.find((pt: any) => pt.label === type)?.value ?? null
      );
    },
    [publicationTypes],
  );

  const initialValues = useMemo(() => {
    if (!isAddMode && currentValues) {
      return {
        publication_id: currentValues.publication_id,
        type: normalizeTypeValue(currentValues.type),
        variant_included: currentValues.variant_included,
        include_in_report: currentValues.include_in_report,
        comment_materialmethod: currentValues.comment_materialmethod || '',
        comment_result: currentValues.comment_result || '',
        comment_evidence: currentValues.comment_evidence || '',
        comment_conclusion: currentValues.comment_conclusion || '',
      };
    }

    return {
      publication_id: pubmedPublication?.id,
      type: undefined,
      variant_included: undefined,
      include_in_report: undefined,
      comment_materialmethod: '',
      comment_result: '',
      comment_evidence: '',
      comment_conclusion: '',
    };
  }, [isAddMode, currentValues, pubmedPublication]);

  // Handle local state for rich text editors
  const [editorStates, setEditorStates] = useState<Record<string, EditorState>>(
    {
      comment_materialmethod: htmlToEditorState(
        initialValues.comment_materialmethod,
      ),
      comment_result: htmlToEditorState(initialValues.comment_result),
      comment_evidence: htmlToEditorState(initialValues.comment_evidence),
      comment_conclusion: htmlToEditorState(initialValues.comment_conclusion),
    },
  );

  const handleEditorChange = useCallback(
    (
      newState: EditorState,
      setFieldValue: (field: string, value: any) => void,
      key: string,
    ) => {
      setEditorStates((prev) => ({ ...prev, [key]: newState }));
      const htmlContent = editorStateToHtml(newState);
      setFieldValue(key, htmlContent);
    },
    [],
  );

  const handleAddPublication = async (values: any) => {
    if (user && variant?.id) {
      try {
        setIsLoading(true);

        const requestData = {
          ...values,
          // values are already HTML strings
        };

        await addPublicationToVariantMutation({
          variant_id: variant.id,
          requestData,
        }).unwrap();

        // invalidatesTags fires automatically — RTK Query refetches VariantReferences.

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: 'Reference has been added successfully',
        });
      } catch (error) {
        console.error(error);
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: 'Failed to add reference. Please try again.',
        });
      } finally {
        setIsLoading(false);
        closeModal();
      }
    }
  };

  const handleUpdatePublication = async (values: any) => {
    if (user && currentValues?.publication_id && variant?.id) {
      try {
        setIsLoading(true);

        const requestData = {
          ...values,
          // values are already HTML strings
          type: normalizeTypeValue(values.type),
        };

        await updatePublicationToVariantMutation({
          variant_publication_id: currentValues?.id,
          requestData,
          variant_id: variant.id,
        }).unwrap();

        // invalidatesTags fires automatically — RTK Query refetches VariantReferences.

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: 'Reference has been updated successfully',
        });
      } catch (error) {
        console.error(error);
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: 'Failed to save reference. Please try again.',
        });
      } finally {
        setIsLoading(false);
        closeModal();
      }
    }
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    if (user) {
      if (isAddMode) {
        handleAddPublication(values);
      } else {
        handleUpdatePublication(values);
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isLoading,
    user,
    stageInfo,
    isAddMode,
    publicationTypes,
    boolOptions,
    titleCitekey,
    titleText,
    createModalOpts,
    initialValues,
    handleEditorChange,
    editorStates,
    getIsSaveDisabled,
    handleFormSubmit,
  };
};

export default useReferenceModalLogic;
